import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildFillInTheBlankPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  const previousVocabSection =
    ctx.previousVocabulary.length > 0
      ? `\n## Previously Introduced Vocabulary (use words from this list in the word bank)

${ctx.previousVocabulary
  .slice(0, 30)
  .map((v) => `- ${v}`)
  .join('\n')}`
      : '';

  return `Generate a **Fill in the Blank** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}${previousVocabSection}

## Requirements

1. Create a **word bank** of exactly **8 words** in ${target?.name}, drawn from the sub-theme "${ctx.subTheme}" and reinforcing vocabulary from earlier blocks.
2. Create exactly **8 sentences** — one per word bank word. Each word is used exactly once.
3. Each sentence must:
   - Be written in ${target?.name} with "___" (three underscores) replacing the missing word
   - Include a \`hint\` in ${source?.name}: the **complete** sentence translated into ${source?.name}, with the word corresponding to the answer wrapped in double asterisks for highlighting (e.g. "He has a bit of **fever** since last night."). Do NOT use blanks ("___") in the hint — show the full translation with the key word highlighted
   - Include the correct \`answer\` matching a word bank entry (case-insensitive match)
4. Sentences should form a coherent mini-narrative or revolve around a common scenario related to "${ctx.subTheme}".
5. The difficulty of sentences must match the learner's ${ctx.profile.level.toUpperCase()} level.
6. Instructions tell the learner to pick the right word from the bank, written in ${source?.name}.
7. Shuffle the word bank so it doesn't follow sentence order.

## Output Format

Return a single JSON object matching this TypeScript interface exactly:

\`\`\`typescript
interface FillInTheBlankBlock {
  id: string;                  // Generate a UUID v4
  type: "fill_in_the_blank";   // Literal
  order: ${ctx.blockOrder};
  title: string;               // In ${target?.name}
  titleNative: string;         // In ${source?.name} with descriptor
  theme: "${ctx.subTheme}";
  estimatedPages: 1;
  instructions: string;        // ⚠️ MUST be in ${source?.name} — NEVER in ${target?.name}
  instructionsNative: string;  // ⚠️ MUST be in ${target?.name} — brief companion version
  wordBank: string[];          // Exactly 8 words, shuffled
  sentences: Array<{
    text: string;              // ${target?.name} sentence with "___"
    hint: string;              // ${source?.name} full translation with answer word wrapped in **asterisks**
    answer: string;            // The correct word from wordBank
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'fill_in_the_blank')}${buildCrossMonthClause(ctx)}`;
}
